import type { Player } from '@api-service/players';

export type PlayerStatus = Player['status'];

/**
 * How loudly a player's availability should be flagged. Mirrors FPL's own
 * colour bands: a mild doubt is a caution (amber), anything worse is a danger
 * (red).
 */
export type AvailabilitySeverity = 'none' | 'caution' | 'danger';

const AVAILABLE: PlayerStatus = 'a';
const DOUBTFUL: PlayerStatus = 'd';
const FULL_CHANCE_OF_PLAYING = 100;
/**
 * FPL reports doubts in 25% steps. A doubt above this threshold (i.e. 75%) is
 * still more likely to play than not, so it only warrants a caution.
 */
const CAUTION_CHANCE_OF_PLAYING_THRESHOLD = 50;

const STATUS_LABELS: Record<Exclude<PlayerStatus, 'a'>, string> = {
  d: 'Doubtful',
  i: 'Injured',
  s: 'Suspended',
  u: 'Unavailable',
  n: 'Not in squad'
};

const getReducedChance = (
  chanceOfPlaying: number | null | undefined
): number | null =>
  typeof chanceOfPlaying === 'number' &&
  chanceOfPlaying < FULL_CHANCE_OF_PLAYING
    ? chanceOfPlaying
    : null;

/**
 * Grades an FPL availability flag. Injured, suspended, unavailable and
 * not-in-squad players are always a danger. A doubtful (or nominally
 * available) player is a caution while their chance of playing stays above
 * 50%, and a danger at 50% or below.
 */
export const getAvailabilitySeverity = (
  status: PlayerStatus | undefined,
  chanceOfPlaying: number | null | undefined
): AvailabilitySeverity => {
  const reducedChance = getReducedChance(chanceOfPlaying);
  const isFlaggedStatus = Boolean(status) && status !== AVAILABLE;

  if (!isFlaggedStatus && reducedChance === null) return 'none';

  const isMildDoubt = !status || status === AVAILABLE || status === DOUBTFUL;
  if (
    isMildDoubt &&
    reducedChance !== null &&
    reducedChance > CAUTION_CHANCE_OF_PLAYING_THRESHOLD
  ) {
    return 'caution';
  }

  return 'danger';
};

/**
 * A player needs a warning when FPL flags them as anything other than fully
 * available: a non-available status, or a reduced chance of playing.
 */
export const hasAvailabilityWarning = (
  status: PlayerStatus | undefined,
  chanceOfPlaying: number | null | undefined
): boolean => getAvailabilitySeverity(status, chanceOfPlaying) !== 'none';

/**
 * The chance of playing as a short badge label, e.g. "75%". Null when FPL
 * gives no reduced chance, which includes some flagged players (a suspension
 * can carry no percentage), so callers need their own fallback for those.
 */
export const getChanceOfPlayingLabel = (
  chanceOfPlaying: number | null | undefined
): string | null => {
  const reducedChance = getReducedChance(chanceOfPlaying);
  return reducedChance === null ? null : `${reducedChance}%`;
};

/** Human-readable availability summary, e.g. "Injured (25%)". Null when fit. */
export const getAvailabilityLabel = (
  status: PlayerStatus | undefined,
  chanceOfPlaying: number | null | undefined
): string | null => {
  const reducedChance = getReducedChance(chanceOfPlaying);

  if (status && status !== AVAILABLE) {
    const base = STATUS_LABELS[status as Exclude<PlayerStatus, 'a'>];
    if (reducedChance !== null && reducedChance > 0) {
      return `${base} (${reducedChance}%)`;
    }
    return base;
  }

  if (reducedChance !== null) return `${reducedChance}% chance of playing`;
  return null;
};
